/**
 * Возвращает ключи сохранённых значений плейсхолдеров для всех экземпляров
 * диагностических блоков текущего шаблона.
 *
 * Эти значения относятся к предыдущему описанию и удаляются перед
 * предзаполнением по «Норме».
 *
 * @param {Array} blocks Текущее состояние диагностических блоков.
 * @param {string} templateId Системное имя шаблона.
 * @returns {string[]} Ключи значений плейсхолдеров в localStorage.
 */
export const getNormaPrefillPlaceholderStorageKeys = (blocks, templateId) =>
  blocks.flatMap(block =>
    block.instances.map(
      instance =>
        `smart_template_placeholders_${templateId}_${block.zone_name}_${instance.id}`
    )
  );

/**
 * Подготавливает диагностические блоки к началу нового описания по «Норме».
 *
 * У каждой зоны остаётся только первый экземпляр с пустым текстом. Его
 * идентификатор и история редактирования сохраняются — это согласованное
 * поведение Undo/Redo. Функция не изменяет переданный массив и его объекты.
 *
 * @param {Array} blocks Текущее состояние диагностических блоков.
 * @param {Function} createInstanceId Создание ID для блока без экземпляров.
 * @returns {Array} Подготовленные диагностические блоки.
 */
export const prepareBlocksForNormaPrefill = (
  blocks,
  createInstanceId = Date.now
) =>
  blocks.map(block => {
    const firstInstance = block.instances[0];

    return {
      ...block,
      instances: [
        {
          ...firstInstance,
          id: firstInstance?.id ?? createInstanceId(),
          text: '',
          history: firstInstance?.history ?? [''],
          historyIndex: firstInstance?.historyIndex ?? 0,
        },
      ],
    };
  });

const appendHtmlLine = (currentText, addedText) =>
  currentText
    ? currentText + (currentText.endsWith('<br>') ? '' : '<br>') + addedText
    : addedText;

/**
 * Последовательно применяет полученные с сервера заготовки «Нормы» к зонам
 * исследования и заключению.
 *
 * Порядок и дубли заготовок сохраняются. Обычные плейсхолдеры обрабатываются
 * переданной общей функцией страницы. Специальная динамическая конструкция
 * для патологий отдельной обработки в этом сценарии не получает.
 *
 * @param {Object} params Параметры предзаполнения.
 * @param {Array} params.blocks Подготовленные диагностические блоки.
 * @param {Array} params.groups Заготовки «Нормы» в серверном порядке.
 * @param {string} params.templateId Системное имя шаблона.
 * @param {Function} params.processPlaceholders Общий обработчик плейсхолдеров.
 * @returns {Array} Новое состояние диагностических блоков.
 */
export const applyNormaPrefillGroups = ({
  blocks,
  groups,
  templateId,
  processPlaceholders,
}) => {
  const newBlocks = [...blocks];
  const conclusionIndex = newBlocks.findIndex(
    block => block.zone_name === `${templateId}_conclusion`
  );
  let conclusionText = '';

  groups.forEach(group => {
    const blockIndex = newBlocks.findIndex(
      block => block.zone_name === group.zone_name
    );

    if (blockIndex !== -1 && group.group_value) {
      const processedGroupValue = processPlaceholders(group.group_value);
      newBlocks[blockIndex] = {
        ...newBlocks[blockIndex],
        instances: newBlocks[blockIndex].instances.map((instance, index) =>
          index === 0
            ? {
                ...instance,
                text: appendHtmlLine(instance.text, processedGroupValue),
              }
            : instance
        ),
      };
    }

    if (group.report_value && conclusionIndex !== -1) {
      conclusionText = appendHtmlLine(
        conclusionText,
        processPlaceholders(group.report_value)
      );
    }
  });

  if (conclusionIndex !== -1 && conclusionText) {
    newBlocks[conclusionIndex] = {
      ...newBlocks[conclusionIndex],
      instances: newBlocks[conclusionIndex].instances.map((instance, index) =>
        index === 0
          ? {
              ...instance,
              text: appendHtmlLine(instance.text, conclusionText),
            }
          : instance
      ),
    };
  }

  return newBlocks;
};
